import { useEffect, useState } from "react";
import {
  X, MessageCircle, Edit3, ShoppingBag, BadgeCheck, Sparkles, AlertTriangle, Star, Clock,
  Calendar, Award, ChevronDown, ChevronUp, Save,
} from "lucide-react";
import { toast } from "sonner";
import { api, unwrap } from "@/lib/api";
import { MemberBadge } from "@/components/MemberBadge";

/**
 * ClientProfilePanel — right-side slide-in.
 * Single-shot fetch from /api/clients/{id}/profile.
 */
export function ClientProfilePanel({
  clientId,
  onClose,
  onCheckIn,
  onBookNow,
  onEnrolMembership,
  onProductSale,
  onUpdated,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    api.get(`/clients/${clientId}/profile`)
      .then((r) => setData(unwrap(r)))
      .catch(() => toast.error("Could not load profile"))
      .finally(() => setLoading(false));
  }, [clientId]);

  const close = () => {
    setExiting(true);
    setTimeout(() => onClose?.(), 220);
  };

  if (!clientId) return null;

  return (
    <>
      <div className="profile-overlay" onClick={close} data-testid="profile-overlay" />
      <aside
        className={`profile-panel ${exiting ? "exiting" : ""}`}
        data-testid="client-profile-panel"
      >
        <PanelHeader onClose={close} />
        {loading || !data ? (
          <div className="px-6 py-20 text-center">
            <div className="font-inter text-[13px] text-[var(--color-text-muted)]">Loading profile…</div>
          </div>
        ) : editMode ? (
          <ProfileEditForm
            client={data.client}
            onCancel={() => setEditMode(false)}
            onSaved={(updated) => {
              setData((d) => ({ ...d, client: updated }));
              setEditMode(false);
              toast.success("Profile updated ✓");
              onUpdated?.(updated);
            }}
          />
        ) : (
          <PanelBody
            data={data}
            onCheckIn={onCheckIn}
            onBookNow={onBookNow}
            onEditProfile={() => setEditMode(true)}
            onEnrolMembership={onEnrolMembership}
            onProductSale={onProductSale}
            historyExpanded={historyExpanded}
            setHistoryExpanded={setHistoryExpanded}
          />
        )}
      </aside>
    </>
  );
}

function PanelHeader({ onClose }) {
  return (
    <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b border-white/40 bg-white/30 backdrop-blur">
      <div className="overline">Client Profile</div>
      <button onClick={onClose} className="w-9 h-9 rounded-full glass-subtle flex items-center justify-center" aria-label="Close" data-testid="profile-close-btn">
        <X size={16} />
      </button>
    </div>
  );
}

function PanelBody({ data, onCheckIn, onBookNow, onEditProfile, onEnrolMembership, onProductSale, historyExpanded, setHistoryExpanded }) {
  const c = data.client || {};
  const membership = data.membership;
  const initials = (c.name || "?").split(" ").filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase();
  const todayApp = data.today_appointment;
  const tags = c.tags || [];
  const isVip = tags.includes("VIP");
  const formula = (data.history || []).find((h) => h.formula_notes)?.formula_notes;
  const lastVisit = (data.history || [])[0];
  const memberExpiringSoon = membership && (() => {
    try {
      const days = Math.ceil((new Date(membership.valid_until) - Date.now()) / 86400000);
      return days <= 14 && days >= 0;
    } catch { return false; }
  })();

  return (
    <div className="pb-24">
      {/* Hero */}
      <div className="px-6 pt-6 pb-5"
           style={{ background: "linear-gradient(135deg, rgba(124,92,122,0.15), rgba(212,168,83,0.10))" }}>
        <div className="flex items-start gap-4">
          <div className={isVip ? "pp-avatar-ring" : ""}>
            <div
              className="w-[72px] h-[72px] rounded-full flex items-center justify-center font-sora font-bold text-white text-[28px]"
              style={{ background: "linear-gradient(135deg,#B08AAE,#7C5C7A,#5E4060)" }}
            >
              {initials}
            </div>
            {membership && (
              <div className="-mt-2 flex justify-center">
                <MemberBadge membership={membership} size="lg" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-sora font-bold text-[22px] leading-tight text-[var(--color-text-primary)]">{c.name}</h2>
            <a href={`tel:${c.phone}`} className="font-mono text-[14px] text-[var(--color-text-muted)] block mt-0.5">{c.phone}</a>
            <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
              {tags.slice(0, 4).map((t) => <Tag key={t} t={t} />)}
            </div>
          </div>
        </div>

        {/* 3 stat chips */}
        <div className="grid grid-cols-3 gap-2 mt-5">
          <StatChip bg="rgba(124,92,122,0.15)" color="#5E4060" v={c.total_visits || 0} l="visits" />
          <StatChip bg="rgba(212,168,83,0.18)" color="#8E6A1A" v={`₹${Math.round(c.total_spend || 0).toLocaleString("en-IN")}`} l="spent" />
          <StatChip bg="rgba(255,255,255,0.55)" color="#44403C" v={c.created_at ? new Date(c.created_at).getFullYear() : "—"} l="since" />
        </div>
      </div>

      {/* Quick Actions */}
      <div className="px-6 py-4 flex items-center gap-2 overflow-x-auto no-scrollbar border-b border-white/40">
        {todayApp && (
          <button onClick={() => onCheckIn?.(todayApp.id)} className="btn-3d btn-gold-3d btn-sm flex-shrink-0" data-testid="profile-checkin-btn">
            Check In
          </button>
        )}
        <button onClick={() => onBookNow?.(c)} className="btn-3d btn-mauve-3d btn-sm flex-shrink-0" data-testid="profile-book-btn">
          <Calendar size={14} /> Book
        </button>
        <button onClick={onEditProfile} className="btn-3d btn-glass-3d btn-sm flex-shrink-0" data-testid="profile-edit-btn">
          <Edit3 size={14} /> Edit
        </button>
        {!membership && (
          <button onClick={() => onEnrolMembership?.(c)} className="btn-3d btn-gold-3d btn-sm flex-shrink-0" data-testid="profile-enrol-btn">
            <Award size={14} /> Enrol Membership
          </button>
        )}
        <button onClick={() => onProductSale?.(c)} className="btn-3d btn-glass-3d btn-sm flex-shrink-0" data-testid="profile-product-btn">
          <ShoppingBag size={14} /> Product Sale
        </button>
        <a href={`https://wa.me/${(c.phone || "").replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer"
           className="btn-3d btn-glass-3d btn-sm flex-shrink-0">
          <MessageCircle size={14} /> WhatsApp
        </a>
      </div>

      {/* Allergy alert */}
      {c.allergies && (
        <div className="mx-6 mt-4 rounded-2xl border border-[rgba(185,28,28,0.30)] bg-[rgba(254,242,242,0.85)] p-4 flex items-start gap-3"
             data-testid="profile-allergy-alert">
          <AlertTriangle size={20} className="text-[var(--color-danger)] flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-sora font-bold text-[13px] text-[var(--color-danger-deep)] uppercase tracking-wider">Allergy on file</div>
            <div className="font-inter text-[13.5px] text-[var(--color-text-primary)] mt-1">{c.allergies}</div>
          </div>
        </div>
      )}

      {/* Preferences card */}
      <Card title="Preferences & Notes" testid="profile-prefs">
        <Row k="Preferred stylist" v={data.preferred_stylist_name || "—"} />
        <Row k="Skin type" v={c.skin_type || "—"} />
        <Row k="Hair type" v={c.hair_type || "—"} />
        <Row k="Scalp" v={c.scalp_condition || "—"} />
        {c.preferred_services?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {c.preferred_services.map((s) => (
              <span key={s} className="px-2 py-0.5 rounded-full text-[11px] font-inter font-medium"
                    style={{ background: "rgba(124,92,122,0.10)", color: "#5E4060" }}>{s}</span>
            ))}
          </div>
        )}
        {c.internal_notes && (
          <div className="mt-3 font-inter italic text-[12.5px] text-[var(--color-text-muted)] leading-relaxed">
            "{c.internal_notes}"
          </div>
        )}
      </Card>

      {/* Last visit */}
      {lastVisit && (
        <Card title="Last visit" testid="profile-last-visit">
          <div className="font-sora font-semibold text-[14px]">
            {(lastVisit.services_requested || []).join(" + ") || "Service"}
          </div>
          <div className="font-inter text-[12.5px] text-[var(--color-text-muted)] mt-0.5">
            {lastVisit.completed_at ? new Date(lastVisit.completed_at).toLocaleDateString("en-IN", { month: "short", day: "numeric", year: "numeric" }) : "—"}
            {lastVisit.assigned_stylist_name && ` · ${lastVisit.assigned_stylist_name}`}
            {lastVisit.amount_due ? ` · ₹${lastVisit.amount_due}` : ""}
          </div>
          {formula && (
            <div className="mt-3 rounded-lg px-3 py-2 font-mono text-[12px] leading-relaxed"
                 style={{ background: "rgba(212,168,83,0.10)", color: "#8E6A1A" }}>
              {formula}
            </div>
          )}
        </Card>
      )}

      {/* Membership */}
      {membership && (
        <Card title="Membership" testid="profile-membership"
              style={{ borderImage: "linear-gradient(135deg,#D4A853,#8E6A1A) 1", borderWidth: 2, borderStyle: "solid", background: "rgba(212,168,83,0.06)" }}>
          <div className="flex items-center gap-3 flex-wrap">
            <MemberBadge membership={membership} size="lg" />
            <span className="font-sora font-semibold text-[14px]">{membership.plan_name}</span>
          </div>
          <div className="font-mono text-[12.5px] text-[var(--color-text-muted)] mt-2">
            Valid until {membership.valid_until} · {membership.discount_percent}% off services
          </div>
          {memberExpiringSoon && (
            <div className="mt-2 rounded-lg px-3 py-2 text-[12.5px]"
                 style={{ background: "rgba(245,158,11,0.14)", color: "#B45309" }}>
              <Clock size={12} className="inline mr-1" /> Renewal due soon — consider offering it now
            </div>
          )}
        </Card>
      )}

      {/* Loyalty points */}
      <Card title="Loyalty points" testid="profile-loyalty">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-[var(--color-gold-mid)]" />
          <span className="font-mono text-[20px] text-[var(--color-gold-deeper)]">
            {(c.loyalty_points || 0).toLocaleString("en-IN")} pts
          </span>
          <span className="font-inter text-[12px] text-[var(--color-text-muted)]">
            = ₹{Math.round((c.loyalty_points || 0) / 10)} redeemable
          </span>
        </div>
      </Card>

      {/* Visit history (collapsible) */}
      {(data.history || []).length > 0 && (
        <Card
          title={`Visit history · ${data.history.length}`}
          testid="profile-history"
          right={
            <button
              onClick={() => setHistoryExpanded(!historyExpanded)}
              className="text-[var(--color-text-muted)]"
              data-testid="profile-history-toggle"
            >
              {historyExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          }
        >
          <div className="relative pl-4">
            <div className="absolute left-1 top-1 bottom-1 w-px bg-[var(--color-gold-mid)]" />
            {(historyExpanded ? data.history : data.history.slice(0, 3)).map((h) => (
              <div key={h.id} className="relative py-2">
                <div className="absolute -left-3 top-3 w-1.5 h-1.5 rounded-full bg-[var(--color-gold-mid)]" />
                <div className="font-inter text-[12.5px] text-[var(--color-text-primary)]">
                  {(h.services_requested || []).join(", ") || "Visit"}
                </div>
                <div className="font-inter text-[11.5px] text-[var(--color-text-muted)]">
                  {h.completed_at ? new Date(h.completed_at).toLocaleDateString("en-IN", { month: "short", day: "numeric" }) : "—"}
                  {h.assigned_stylist_name && ` · ${h.assigned_stylist_name}`}
                  {h.amount_due ? ` · ₹${h.amount_due}` : ""}
                  {h.feedback_score && (
                    <span className="ml-2 inline-flex items-center gap-0.5 text-[var(--color-gold-deeper)]">
                      <Star size={10} fill="currentColor" /> {h.feedback_score}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Retail purchases */}
      <Card title={`Products purchased · ${(data.retail_purchases || []).length}`} testid="profile-retail">
        {(data.retail_purchases || []).length === 0 ? (
          <div className="font-inter text-[12.5px] text-[var(--color-text-muted)] py-1">
            No retail purchases yet
          </div>
        ) : (
          <div className="relative pl-4">
            <div className="absolute left-1 top-1 bottom-1 w-px bg-[var(--color-mauve-soft)]" />
            {data.retail_purchases.map((s) => {
              const li = (s.line_items || [])[0] || {};
              return (
                <div key={s.id} className="relative py-2">
                  <div className="absolute -left-3 top-3 w-1.5 h-1.5 rounded-full bg-[var(--color-mauve-soft)]" />
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-inter text-[12.5px] text-[var(--color-text-primary)] truncate">
                        {li.product_name || s.primary_product_name}
                        {li.product_brand && (
                          <span className="font-inter text-[11px] text-[var(--color-text-muted)]"> · {li.product_brand}</span>
                        )}
                      </div>
                      <div className="font-inter text-[11.5px] text-[var(--color-text-muted)]">
                        {s.sold_at ? new Date(s.sold_at).toLocaleDateString("en-IN", { month: "short", day: "numeric" }) : "—"}
                        {s.sold_by_staff_name && ` · Sold by ${s.sold_by_staff_name}`}
                      </div>
                    </div>
                    <div className="font-mono text-[12.5px] text-[var(--color-gold-deeper)] flex-shrink-0">
                      ₹{Math.round(s.total_amount)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

function ProfileEditForm({ client, onSaved, onCancel }) {
  const [f, setF] = useState({
    name: client.name || "",
    email: client.email || "",
    dob: client.dob || "",
    anniversary: client.anniversary || "",
    skin_type: client.skin_type || "",
    hair_type: client.hair_type || "",
    scalp_condition: client.scalp_condition || "",
    allergies: client.allergies || "",
    internal_notes: client.internal_notes || "",
    occupation: client.occupation || "",
    location: client.location || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/clients/${client.id}`, f);
      onSaved(unwrap(r));
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const Field = ({ label, k, mono, type = "text", rows }) => (
    <div className="mb-3">
      <label className="block font-inter text-[11.5px] text-[var(--color-text-muted)] mb-1">{label}</label>
      {rows ? (
        <textarea
          rows={rows} value={f[k] || ""} onChange={(e) => setF((p) => ({ ...p, [k]: e.target.value }))}
          className="w-full glass-subtle rounded-lg px-3 py-2 font-inter text-[13.5px] outline-none focus:ring-2 focus:ring-[var(--color-mauve-soft)]"
        />
      ) : (
        <input
          type={type} value={f[k] || ""} onChange={(e) => setF((p) => ({ ...p, [k]: e.target.value }))}
          className={`w-full glass-subtle rounded-lg px-3 py-2 text-[13.5px] outline-none focus:ring-2 focus:ring-[var(--color-mauve-soft)] ${mono ? "font-mono" : "font-inter"} ${k === "allergies" && f.allergies ? "ring-2 ring-[rgba(185,28,28,0.30)]" : ""}`}
        />
      )}
    </div>
  );

  return (
    <div className="px-6 py-5 pb-32" data-testid="profile-edit-form">
      <div className="overline mb-4">Edit profile</div>
      <Field label="Name" k="name" />
      <Field label="Email" k="email" type="email" />
      <Field label="DOB (YYYY-MM-DD)" k="dob" mono />
      <Field label="Anniversary" k="anniversary" mono />
      <Field label="Occupation" k="occupation" />
      <Field label="Location" k="location" />
      <Field label="Skin type" k="skin_type" />
      <Field label="Hair type" k="hair_type" />
      <Field label="Scalp condition" k="scalp_condition" />
      <Field label="⚠ Allergies (red if any)" k="allergies" />
      <Field label="Internal notes" k="internal_notes" rows={3} />

      <div className="fixed bottom-0 right-0 w-[480px] max-w-full px-6 py-4 border-t border-white/40 bg-white/55 backdrop-blur flex gap-3 justify-end">
        <button onClick={onCancel} className="btn-3d btn-glass-3d btn-sm">Cancel</button>
        <button onClick={save} disabled={saving} className="btn-3d btn-gold-3d btn-sm" data-testid="profile-save-btn">
          <Save size={14} /> {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

function Card({ title, children, right, testid, style }) {
  return (
    <div className="mx-6 mt-4 rounded-2xl glass-subtle px-4 py-4" data-testid={testid} style={style}>
      <div className="flex items-center justify-between mb-2">
        <div className="overline">{title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between py-1 text-[12.5px]">
      <span className="font-inter text-[var(--color-text-muted)]">{k}</span>
      <span className="font-inter text-[var(--color-text-primary)] text-right">{v}</span>
    </div>
  );
}

function Tag({ t }) {
  const colorMap = {
    VIP: { bg: "rgba(212,168,83,0.14)", color: "#B5892E" },
    "colour-client": { bg: "rgba(124,92,122,0.12)", color: "#5E4060" },
    membership: { bg: "rgba(212,168,83,0.18)", color: "#8E6A1A" },
    bridal: { bg: "rgba(244,114,182,0.14)", color: "#BE185D" },
    regular: { bg: "rgba(255,255,255,0.5)", color: "#44403C" },
    new: { bg: "rgba(34,197,94,0.10)", color: "#15803D" },
    emergency: { bg: "rgba(185,28,28,0.10)", color: "#991B1B" },
  };
  const c = colorMap[t] || colorMap.regular;
  return (
    <span className="status-pill" style={{ background: c.bg, color: c.color }}>{t}</span>
  );
}

function StatChip({ bg, color, v, l }) {
  return (
    <div className="rounded-xl px-3 py-2.5 text-center" style={{ background: bg }}>
      <div className="font-mono text-[16px] font-medium" style={{ color }}>{v}</div>
      <div className="font-inter text-[10.5px] text-[var(--color-text-muted)] uppercase tracking-wider mt-0.5">{l}</div>
    </div>
  );
}
